import { ChangeEvent, useEffect, useState } from 'react'
import { PaginatedTable } from '../../common/components/table/PaginatedTable'
import { goodsHoldersTableColumns } from './GoodsHoldersTableColumns'
import { useSearchGoodsHoldersLazyQuery } from '../../generated/graphql'
import classNames from 'classnames'
import { Link, Select, Snack } from '@nike/eds'
import { ApolloResult } from '../../common/ApolloResult'
import {
  mapObjectValues,
  textInputToSearchParams,
  useQueryParameters,
} from '../../common/hooks/QueryParamsHook'
import { MultilineSearchInput } from '../../common/components/multiline-input/MultilineSearchInput'
import { shipStatusMapping } from '../../common/utils/formatter'

const SPLIT_REGEX = /[\s,;\t]+/
const statusOptions = [
  {
    value: 'CREATED',
    label: 'Created',
  },
  {
    value: 'LOADED',
    label: 'Loaded',
  },
  {
    value: 'SHIPPED',
    label: 'Shipped',
  },
  {
    value: 'DELIVERED',
    label: 'Delivered',
  },
  {
    value: 'CANCELLED',
    label: 'Cancelled',
  },
]

function GoodsHolderSearchFields() {
  const [{ searchParameters }, setQueryParameters] = useQueryParameters()
  const [inputState, setInputState] = useState<Record<string, string>>(
    mapObjectValues(searchParameters, (searchParamValue) => searchParamValue.join(','))
  )

  function clearTextAreasSearchParameters() {
    setInputState({})
    setQueryParameters({ searchParameters: {}, paging: { from: 0 } })
  }

  return (
    <div
      id="search-fields"
      style={{
        display: 'flex',
        columnGap: '16px',
        marginBottom: '16px',
      }}
    >
      <MultilineSearchInput
        name="goodsHolderNumbers"
        label="Carton / Tracking Numbers"
        value={inputState.goodsHolderNumbers}
        onChange={updateTextAreaSearchParameter}
      />
      <MultilineSearchInput
        name="proNumbers"
        label="CMRs"
        value={inputState.proNumbers}
        onChange={updateTextAreaSearchParameter}
      />
      <MultilineSearchInput
        name="vendorAccessNumbers"
        label="Trailer Access Numbers"
        value={inputState.vendorAccessNumbers}
        onChange={updateTextAreaSearchParameter}
      />
      <div style={{ flexGrow: 1 }}>
        <Select
          id="isMulti-example"
          name="calculatedGoodsHolderStatusCodes"
          value={mapGoodsHolderStatus(inputState.calculatedGoodsHolderStatusCodes)}
          className={'eds-select-overwrite'}
          isMulti
          options={statusOptions}
          label="Status"
          onChange={updateSelectParameter}
        />
      </div>
      <div style={{ display: 'flex', alignContent: 'center', marginTop: '30px' }}>
        <Link as="button" variant="secondary" onClick={clearTextAreasSearchParameters}>
          Clear Filters
        </Link>
      </div>
    </div>
  )

  function mapGoodsHolderStatus(statusParameter: string) {
    if (!statusParameter) {
      return null
    }
    const statusArray = statusParameter.split(SPLIT_REGEX)
    return statusArray.map((s) => {
      return {
        value: s,
        label: shipStatusMapping[s],
      }
    })
  }

  function updateTextAreaSearchParameter(event: ChangeEvent<HTMLTextAreaElement>) {
    const newInputState = {
      ...inputState,
      [event.target.name]: event.target.value,
    }
    setInputState(newInputState)
    setQueryParameters({
      searchParameters: textInputToSearchParams(newInputState),
      paging: { from: 0 },
    })
  }

  function updateSelectParameter(newValue: any, event: any) {
    const newInputState = {
      ...inputState,
      [event.name]: newValue.map(({ value }) => value).toString(),
    }
    setInputState(newInputState)
    setQueryParameters({
      searchParameters: textInputToSearchParams(newInputState),
      paging: { from: 0 },
    })
  }
}

export function GoodsHoldersTable({ packlistNumber }) {
  const [{ paging, searchParameters }] = useQueryParameters()
  const [fetchGoodsHolders, goodsHolderQueryResult] = useSearchGoodsHoldersLazyQuery({
    variables: {
      packlistNumber,
      ...searchParameters,
      from: paging.from,
      size: paging.size,
    },
    context: {
      page: 'goodsholders',
    },
  })
  const goodsHolders = goodsHolderQueryResult?.data?.searchGoodsHolders

  //Use effect to force refresh when the page is revisited
  useEffect(() => {
    fetchGoodsHolders()
  }, [fetchGoodsHolders, packlistNumber])

  return (
    <div>
      <GoodsHolderSearchFields />
      <ApolloResult {...goodsHolderQueryResult}>
        {goodsHolders?.length > 0 ? (
          <PaginatedTable
            columns={goodsHoldersTableColumns}
            data={goodsHolders}
            paging={paging}
            getRowProps={(row) => ({
              className: classNames(
                row.cells[0].value?.length ? 'tt-exception' : '',
                row.cells?.find(({ column }) => column.Header === 'Status').value?.toLowerCase()
              ),
            })}
            className="goods-holders-table"
          />
        ) : (
          <Snack id="no-cartons-snack" hideDismiss={true} status="info">
            No cartons found
          </Snack>
        )}
      </ApolloResult>
    </div>
  )
}
